import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MigrationHttpService} from './migration-http.service';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {AlertLevel} from '../../enums/alert-level';
import {LegacyClaimStatus} from './migration.models';

/**
 * Shown once after a successful claim (#279): confirms it worked and offers to switch password login off.
 *
 * Reached two ways, and it deliberately cannot tell them apart. A provider claim RETURNS here — the OIDC
 * callback redirects back with `?claimed=success` — while the native (e-mail + password) claim never left the
 * page and simply switches this panel on. What was accomplished is the same in both cases, so the copy below
 * names no method; see the comment on the confirmation sentence.
 *
 * The offer is deliberately an offer. Switching the password off automatically would be the strongest push
 * toward accounts and also the most damaging default, because a legacy management login is shared — the first
 * person to migrate would lock out every colleague who has not claimed it yet.
 *
 * Transitional — deleted together with the legacy authorization system at #264.
 */
@Component({
  selector: 'auth-claim-result',
  standalone: true,
  template: `
    <div class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         role="dialog" aria-modal="true" aria-labelledby="claim-result-title">
      <div class="max-h-full w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-lg sm:rounded-2xl sm:p-6">

        <h2 id="claim-result-title" class="text-lg font-semibold text-gray-900">Konto verknüpft</h2>

        <!-- This sentence named Microsoft unconditionally, and was shown after EVERY claim — so an Apple or a
             Google claim already read back the wrong provider, and the native claim (#279) made it wrong in a
             fourth way. Nothing here knows which door was used: a ClaimingAccount carries no provider,
             deliberately, because the account model does not care. So the honest fix is not to plumb the
             provider through and print it, but to stop asserting one — what is true after every claim is that
             this login now opens with the account that just claimed it. -->
        <p class="mt-2 text-sm text-gray-600">
          Sie können sich ab sofort mit Ihrem Konto anmelden. Ihr bisheriges Passwort funktioniert vorerst
          weiter.
        </p>

        @if (status?.canDisablePasswordLogin && status?.passwordLoginEnabled) {
          <div class="mt-4 rounded-lg border-[1px] border-gray-100 bg-gray-50 p-3">
            <p class="text-sm font-medium text-gray-900">Passwort-Login abschalten?</p>
            <p class="mt-1 text-xs text-gray-500">
              Danach ist die Anmeldung nur noch über verknüpfte Konten möglich.
              @if (sharedLogin) {
                <span class="font-semibold text-gray-700">
                  Achtung: dieser Zugang wird von mehreren Personen genutzt — wer ihn noch nicht verknüpft hat,
                  kommt danach nicht mehr hinein.
                </span>
              }
            </p>

            <div class="mt-3 flex flex-col gap-2 sm:flex-row-reverse">
              <button type="button" (click)="disable()" [disabled]="busy"
                      class="rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50 sm:flex-1">
                Passwort-Login abschalten
              </button>
              <button type="button" (click)="closed.emit()" [disabled]="busy"
                      class="rounded-md border-[1px] border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:flex-1">
                Aktiviert lassen
              </button>
            </div>
          </div>
        } @else {
          <button type="button" (click)="closed.emit()"
                  class="mt-5 w-full rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900">
            Weiter
          </button>
        }
      </div>
    </div>
  `
})
export class ClaimResultComponent {
  @Input() status: LegacyClaimStatus | null = null;

  @Output() closed = new EventEmitter<void>();

  protected busy = false;

  /** More than one account can open this login, so switching the password off affects other people. */
  protected get sharedLogin(): boolean {
    return (this.status?.claimedBy.length ?? 0) > 1;
  }

  constructor(private http: MigrationHttpService, private gas: GlobalAlertService) {
  }

  protected disable(): void {
    if (this.busy) return;
    this.busy = true;

    this.http.disablePassword().subscribe({
      next: () => {
        this.gas.createAndShow('Erledigt', 'Passwort-Login wurde abgeschaltet.', AlertLevel.success);
        this.closed.emit();
      },
      // The server keeps its own last-authenticator safety rule, so a refusal here is meaningful rather than a
      // glitch — say so and leave the login as it was.
      error: () => {
        this.gas.createAndShow('Fehler', 'Passwort-Login konnte nicht abgeschaltet werden.', AlertLevel.error);
        this.busy = false;
      }
    });
  }
}
